import { LicenceStatus, type Licence } from 'src/__generated__/graphql';
import {
  Page,
  PageSection,
  PageSectionState,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useProfileAnalytic } from 'src/hooks/useProfileAnalytic';
import {
  IMPRESSION_PROFILE_CERTSY,
  TrackingVerificationStatus,
} from 'src/modules/analytics.types';

const LINK_PROFILE_LICENCE_ADD = 'profile-licence-add';
const LINK_PROFILE_LICENCE_EDIT = 'profile-licence-edit';
const LINK_PROFILE_LICENCE_DELETE = 'profile-licence-delete';

const EVENT_PROFILE_INFO_SUCCEEDED = 'profile_update_succeeded';
const EVENT_PROFILE_LICENCES_VIEWED = 'profile_section_displayed';

const PAGE_TITLE_LICENCE = 'profile+licences';
const ACTION_ORIGIN = 'steps:applicationForm';
const PROFILE_UPDATE_SOURCE = 'steps';

const TRACK_VIEW_CONTEXT = {
  actionOrigin: ACTION_ORIGIN,
  profileUpdateSource: PROFILE_UPDATE_SOURCE,
  currentPageSection: PageSection.LICENCES,
};

const TRACK_LINK_CONTEXT = {
  pageTitle: PAGE_TITLE_LICENCE,
  actionOrigin: ACTION_ORIGIN,
  profileUpdateSource: PROFILE_UPDATE_SOURCE,
  currentPage: Page.PROFILE_PAGE,
  currentPageSection: PageSection.LICENCES,
};

export interface LicenceTracking {
  trackViewList: TrackViewList;
  trackViewAdd: () => void;
  trackViewEdit: TrackViewEdit;
  trackAdd: TrackAdd;
  trackEdit: TrackEdit;
  trackDelete: TrackDelete;
}

export interface TrackingLicenceAttributes {
  certsyType: string;
  verificationStatus: TrackingVerificationStatus;
  isLicenceExpired: boolean;
  licencesVerifiedTotal: number;
  licencesNotVerifiedTotal: number;
  licencesUnverifiableTotal: number;
  licencesExpiredTotal: number;
}

type TrackViewList = ({ licences }: { licences: Licence[] }) => void;
type TrackViewEdit = ({ licence }: { licence?: Licence }) => void;
type TrackAdd = ({ licence }: { licence?: Licence }) => void;
type TrackEdit = ({ licence }: { licence?: Licence }) => void;
type TrackDelete = ({ licence }: { licence?: Licence }) => void;

const useTrackViewList = (): TrackViewList => {
  const client = useTrackingClient();
  const currentPage = useGetCurrentPage();

  return ({ licences }) => {
    const licencesVerifiedTotal = licences.filter((licence) =>
      Boolean(licence?.credential),
    ).length;

    const licencesNotVerifiedTotal = licences.filter(
      (licence) =>
        !Boolean(licence?.credential) && Boolean(licence?.verificationUrl),
    ).length;

    const licencesWithoutVerificationUrl = licences.filter(
      (licence) => !Boolean(licence?.verificationUrl),
    ).length;

    const licencesExpiredTotal = licences.filter(
      (licence) => licence.status === LicenceStatus.Expired,
    ).length;

    client.trackLink({
      link: IMPRESSION_PROFILE_CERTSY,
      eventName: IMPRESSION_PROFILE_CERTSY,
      licencesVerifiedTotal,
      licencesNotVerifiedTotal,
      licencesUnverifiableTotal:
        licencesWithoutVerificationUrl - licencesVerifiedTotal,
      licencesExpiredTotal,
      ...TRACK_LINK_CONTEXT,
      currentPage,
      currentPageSection: PageSection.CERTSY,
    });
  };
};

const getVerificationStatus = ({ licence }: { licence?: Licence }) => {
  if (licence?.credential) {
    return TrackingVerificationStatus.VERIFIED;
  }

  if (licence?.verificationUrl) {
    return TrackingVerificationStatus.NOT_VERIFIED;
  }

  return TrackingVerificationStatus.UNVERIFIABLE;
};

const getLicenceTrackingAttributes = ({
  licence,
}: {
  licence?: Licence;
}): Partial<TrackingLicenceAttributes> => ({
  certsyType: licence?.credentialType ? licence.credentialType : undefined,
  verificationStatus: getVerificationStatus({ licence }),
  isLicenceExpired: licence?.status === LicenceStatus.Expired,
});

const useTrackViewAdd = () => {
  const client = useTrackingClient();
  const currentPage = useGetCurrentPage();
  return () => {
    client.trackView({
      view: currentPage,
      eventName: EVENT_PROFILE_LICENCES_VIEWED,
      currentPageSectionState: PageSectionState.ADD,
      ...TRACK_VIEW_CONTEXT,
    });
  };
};

const useTrackViewEdit = (): TrackViewEdit => {
  const client = useTrackingClient();
  const currentPage = useGetCurrentPage();
  return ({ licence }) => {
    client.trackView({
      view: currentPage,
      eventName: EVENT_PROFILE_LICENCES_VIEWED,
      currentPageSectionState: PageSectionState.EDIT,
      ...TRACK_VIEW_CONTEXT,
      ...getLicenceTrackingAttributes({ licence }),
    });
  };
};

const useTrackAdd = (): TrackAdd => {
  const client = useTrackingClient();
  const currentPage = useGetCurrentPage();
  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();
  return ({ licence }) => {
    client.trackLink({
      link: LINK_PROFILE_LICENCE_ADD,
      eventName: EVENT_PROFILE_INFO_SUCCEEDED,
      updateType: UpdateType.ADD,
      profileApproachabilityStatus,
      profileSharedStatus,
      profileVisibilityLevel,
      ...TRACK_LINK_CONTEXT,
      currentPage,
      ...getLicenceTrackingAttributes({ licence }),
    });
  };
};

const useTrackEdit = (): TrackEdit => {
  const client = useTrackingClient();
  const currentPage = useGetCurrentPage();
  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();
  return ({ licence }) => {
    client.trackLink({
      link: LINK_PROFILE_LICENCE_EDIT,
      eventName: EVENT_PROFILE_INFO_SUCCEEDED,
      updateType: UpdateType.EDIT,
      profileApproachabilityStatus,
      profileSharedStatus,
      profileVisibilityLevel,
      ...TRACK_LINK_CONTEXT,
      currentPage,
      ...getLicenceTrackingAttributes({ licence }),
    });
  };
};

const useTrackDelete = (): TrackDelete => {
  const client = useTrackingClient();
  const currentPage = useGetCurrentPage();
  const {
    profileApproachabilityStatus,
    profileSharedStatus,
    profileVisibilityLevel,
  } = useProfileAnalytic();
  return ({ licence }) => {
    client.trackLink({
      link: LINK_PROFILE_LICENCE_DELETE,
      eventName: EVENT_PROFILE_INFO_SUCCEEDED,
      updateType: UpdateType.DELETE,
      profileApproachabilityStatus,
      profileSharedStatus,
      profileVisibilityLevel,
      ...TRACK_LINK_CONTEXT,
      currentPage,
      ...getLicenceTrackingAttributes({ licence }),
    });
  };
};

export const useLicenceTracking = (): LicenceTracking => {
  const trackViewList = useTrackViewList();
  const trackViewAdd = useTrackViewAdd();
  const trackViewEdit = useTrackViewEdit();
  const trackAdd = useTrackAdd();
  const trackEdit = useTrackEdit();
  const trackDelete = useTrackDelete();

  return {
    trackViewList,
    trackViewAdd,
    trackViewEdit,
    trackAdd,
    trackEdit,
    trackDelete,
  };
};
